<script setup lang="ts">
import EmptyState from '@/components/EmptyState.vue'
import { Paginated } from '@/types'
import InvestorNewsController from '@controllers/Investors/InvestorNewsController'
import { Head, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import NewsItemData = App.Data.News.NewsItemData

const { t } = useI18n()

defineProps<{
  newsItems: Paginated<NewsItemData>
  filters: {
    per_page: number
  }
}>()

function onPage(event: { page: number; rows: number }) {
  router.get(
    InvestorNewsController.index().url,
    {
      page: event.page + 1,
      per_page: event.rows,
    },
    {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    },
  )
}
</script>

<template>
  <Head :title="t('title')" />

  <div class="flex flex-col gap-6">
    <Card>
      <template #title>
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span>{{ t('title') }}</span>
          <span class="text-surface-500 text-sm font-normal">
            {{ t('count', { count: newsItems.total }) }}
          </span>
        </div>
      </template>
      <template #content>
        <div v-if="newsItems.data.length" class="flex flex-col gap-4">
          <article
            v-for="item in newsItems.data"
            :key="item.source_url"
            class="border-surface-200 flex flex-col gap-4 rounded-xl border p-4">
            <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div class="flex flex-col gap-2">
                <div class="text-surface-500 flex items-center gap-2 text-xs">
                  <img
                    v-if="item.source.favicon_base64"
                    :src="item.source.favicon_base64"
                    :alt="item.source.name"
                    class="h-4 w-4 rounded-sm" />
                  <span>{{ item.source.name }}</span>
                  <span v-if="item.published_at">•</span>
                  <span v-if="item.published_at">
                    {{ new Date(item.published_at).toLocaleDateString('de-DE') }}
                  </span>
                </div>
                <h1 class="text-surface-900 text-lg font-semibold">
                  {{ item.title }}
                </h1>
              </div>
              <a
                :href="item.source_url"
                target="_blank"
                rel="noreferrer"
                class="text-action hover:text-action-hover text-sm font-medium">
                {{ t('readSource') }}
              </a>
            </div>

            <p class="text-surface-800 text-sm leading-6">
              {{ item.full_summary }}
            </p>

            <div class="flex flex-wrap gap-2">
              <Tag v-for="category in item.categories" :key="category" :value="category" severity="info" />
            </div>
          </article>

          <Paginator
            :rows="filters.per_page"
            :total-records="newsItems.total"
            :first="(newsItems.current_page - 1) * filters.per_page"
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            @page="onPage" />
        </div>

        <EmptyState v-else icon="pi pi-megaphone" :title="t('emptyTitle')" :description="t('empty')" />
      </template>
    </Card>
  </div>
</template>

<i18n>
{
  "en": {
    "title": "Market Briefing",
    "count": "{count} articles",
    "readSource": "Open source",
    "emptyTitle": "No articles available",
    "empty": "No articles are available yet."
  },
  "de": {
    "title": "Marktüberblick",
    "count": "{count} Beiträge",
    "readSource": "Originalquelle öffnen",
    "emptyTitle": "Keine Beiträge verfügbar",
    "empty": "Aktuell sind noch keine Beiträge verfügbar."
  }
}
</i18n>
